import { useState } from "react";
import type { Moneda } from "@/lib/api";
import { Row } from "./Stack";
import { Select } from "./Select";
import { TextField } from "./TextField";

/** Texto parcial válido mientras se escribe: dígitos, coma o punto, hasta 2 decimales. */
const PARCIAL = /^\d{0,15}([.,]\d{0,2})?$/;
// Sólo para MOSTRAR con miles cuando el input no tiene foco. El valor que viaja sigue siendo el texto crudo.
const MONTO = new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Local al catálogo (no importa lib/gastos): un componente de src/components no depende de un dominio.
const OPCIONES_MONEDA = [
  { valor: "ARS", etiqueta: "ARS" },
  { valor: "USD", etiqueta: "USD" },
];

export interface MontoFieldProps {
  etiqueta: string;
  /** Texto crudo ("1234,5"); para mandar a la API usar parseMonto() de lib/gastos. */
  valor: string;
  onCambio: (valor: string) => void;
  moneda: Moneda;
  /** Si falta, la moneda se muestra fija (ej. pagos, que van en la moneda del gasto). */
  onCambioMoneda?: (moneda: Moneda) => void;
  error?: string | null;
  ayuda?: string;
  deshabilitado?: boolean;
}

/** Monto con formato es-AR: mientras se escribe, texto crudo; sin foco, con puntos de miles. Nunca float. */
export function MontoField({ etiqueta, valor, onCambio, moneda, onCambioMoneda, error, ayuda, deshabilitado }: MontoFieldProps) {
  const [conFoco, setConFoco] = useState(false);
  const numero = Number(valor.replace(",", "."));
  // "," o "." solos son parciales válidos mientras se escribe (ver PARCIAL) pero no son un número: mostrar el crudo, nunca "NaN".
  const mostrado = conFoco || !valor || Number.isNaN(numero) ? valor : MONTO.format(numero);
  return (
    <Row alinear="inicio" espacio={2}>
      <TextField
        etiqueta={etiqueta}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={mostrado}
        onFocus={() => setConFoco(true)}
        onBlur={() => setConFoco(false)}
        onChange={(e) => {
          const nuevo = e.target.value.trim();
          if (PARCIAL.test(nuevo)) onCambio(nuevo);
        }}
        error={error}
        ayuda={ayuda}
        disabled={deshabilitado}
        contenedorClassName="flex-1"
      />
      {onCambioMoneda ? (
        <Select etiqueta="Moneda" opciones={OPCIONES_MONEDA} valor={moneda} onCambio={(v) => onCambioMoneda(v as Moneda)} deshabilitado={deshabilitado} className="w-28" />
      ) : (
        <span className="mt-8 h-11 leading-[2.75rem] text-texto-suave">{moneda}</span>
      )}
    </Row>
  );
}
